import React from 'react';

import { Responsive, responsiveClasses, withBaseDefault } from '../helpers/responsive';
import {
  propOptions,
  propDefaults,
  getClassnames,
  modifierClasses,
  variantToClasses,
  CanonicalTextStyle,
  TextColor,
} from './textVariants';

export type { CanonicalTextStyle, TextColor } from './textVariants';

type FontWeights = 'regular' | 'medium' | 'semibold' | 'bold';
type FontSizes = '300' | '350' | '400' | '450' | '500' | '550' | '600' | '700' | '800' | '900' | '1000' | '1200' | '1600';
type AllowedHtmlTags = 'div' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface RCTextProps {
  /** Specifies the HTML tag used by the text element. Defaults to 'div'.
   *  Options are limited by text style.
  */
  htmlTag?: AllowedHtmlTags,
  /** Text color — a fixed semantic color (`secondary`, `accent`, …) or `themed`, the
   *  surrounding themed surface's family color (e.g. notice text on a notice-ultralight
   *  fill — only meaningful inside a themed surface, otherwise falls back to the body
   *  text color). Emitted as a utility class so it composes via the cascade (never
   *  inline style). Inherits color when unset; off-enum colors go through `className`. */
  color?: TextColor,
  /** Optionally, pass a numeral to truncate text to the specified number of lines. */
  maxLines?: 1 | 2 | 3 | 4 | 5,
  /** Passes through to the htmlTag. */
  id?: string;
  /** Escape hatch for adjacent, non-text-style properties the typed props don't cover
   *  (`text-transform`, `text-align`, overflow/ellipsis, spacing) — matching RCFlex.
   *  Prefer the typed props for the component's own domain (variant / size / weight). */
  className?: string,
}

// Base RCText — the general text primitive. Its `htmlTag` is NOT constrained by variant
// (any allowed tag), and its style is driven entirely by the flat `variant` token. The
// default-variant sugar (constrained size/weight) lives on RCText.Default.
type BaseTextProps = RCTextProps & {
  /** The base is the general text primitive, so its tag is NOT constrained by variant
   *  (unlike the subcomponents): any allowed tag is valid, since a responsive/cross-variant
   *  `variant` (e.g. a `title-*`/`display-*` style) routinely wants a semantic heading tag
   *  like `h1`. Tag = semantics, variant = presentation — the two are decoupled. */
  htmlTag?: AllowedHtmlTags;
  /** The text style, as a flat `{variant}-{size}[-{weight}]` token — optionally
   *  responsive (`{ desktop, tablet, mobile }`). This is the general text primitive:
   *  it can render ANY variant (including cross-variant switches per breakpoint, e.g.
   *  `{ desktop: 'display-1600', mobile: 'title-900' }`) — the escape from a
   *  `useMediaQuery` render-swap. The subcomponents (`RCText.Title`, …) are static
   *  sugar over this. Defaults to `default-400`. */
  variant?: Responsive<CanonicalTextStyle>;
};

// RCText.Default — static sugar for the default variant (parallel to .Title/.Utility):
// constrained size/weight + div/span/p tags. Use bare `<RCText>` for plain body text;
// reach for `<RCText.Default size=…>` when you need a non-default default-variant size/weight.
type DefaultTextProps = RCTextProps & {
  htmlTag?: Extract<AllowedHtmlTags, typeof propOptions.default.htmlTags[number]>;
  size?: Responsive<Extract<FontSizes, typeof propOptions.default.sizes[number]>>;
  weight?: Responsive<Extract<FontWeights, typeof propOptions.default.weights[number]>>;
};

type DisplayTextProps = RCTextProps & {
  htmlTag?: Extract<AllowedHtmlTags, typeof propOptions.display.htmlTags[number]>;
  size?: Responsive<Extract<FontSizes, typeof propOptions.display.sizes[number]>>;
  weight?: Responsive<Extract<FontWeights, typeof propOptions.display.weights[number]>>;
};

export type EyebrowTextProps = RCTextProps & {
  htmlTag?: Extract<AllowedHtmlTags, typeof propOptions.eyebrow.htmlTags[number]>;
  size?: Responsive<Extract<FontSizes, typeof propOptions.eyebrow.sizes[number]>>;
  weight?: Responsive<Extract<FontWeights, typeof propOptions.eyebrow.weights[number]>>;
};

type FineTextProps = RCTextProps & {
  htmlTag?: Extract<AllowedHtmlTags, typeof propOptions.fine.htmlTags[number]>;
  size?: Responsive<Extract<FontSizes, typeof propOptions.fine.sizes[number]>>;
  weight?: Responsive<Extract<FontWeights, typeof propOptions.fine.weights[number]>>;
};

type TitleTextProps = RCTextProps & {
  htmlTag?: Extract<AllowedHtmlTags, typeof propOptions.title.htmlTags[number]>;
  size: Responsive<Extract<FontSizes, typeof propOptions.title.sizes[number]>>;
  weight?: Responsive<Extract<FontWeights, typeof propOptions.title.weights[number]>>;
};

type UtilityTextProps = RCTextProps & {
  htmlTag?: Extract<AllowedHtmlTags, typeof propOptions.utility.htmlTags[number]>;
  size: Responsive<Extract<FontSizes, typeof propOptions.utility.sizes[number]>>;
  weight?: Responsive<Extract<FontWeights, typeof propOptions.utility.weights[number]>>;
};

/**
 *  Utility component — renders text through the canonical `text-*` utility classes via a
 *  constrained size / weight / variant API (and picks the semantic tag). No structural contract.
 *
 *  Use this component for displaying text. Dot notation variants allow for more specific styling cases:
 *
 *  - RCText (base): The general text primitive. Its flat `variant` prop renders any
 *    text style and is the only one that goes responsive (per-breakpoint / cross-variant).
 *    Bare `<RCText>` is default body text.
 *  - RCText.**Default**: Static sugar for the default variant with constrained size/weight
 *    (`div`/`span`/`p`). Reach for it when you need a non-default default-variant size/weight.
 *  - RCText.**Display**: Extra large responsive text used for page headings. Often uses an `h1` HTML tag.
 *  - RCText.**Eyebrow**: Small and uppercase text. Often uses a `span` tag.
 *  - RCText.**Fine**: Very small text. Used for disclaimers.
 *  - RCText.**Title**: Large responsive text used for headings within a page layout. Often uses an `h1 - h6` HTML tag.
 *  - RCText.**Utility**: Flexible size and weight options with a shorter line height. Especially useful in UI compositions.
 */
export function RCText({
  htmlTag = 'div',
  variant = 'default-400',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<BaseTextProps>) {
  const Tag = htmlTag as React.ElementType;

  // The flat `variant` token drives the style. `withBaseDefault` injects `default-400`
  // at the desktop tier for a partial object that omits it (e.g. `{ mobile: … }`), so the
  // base keeps the component's default style rather than rendering with no `text-*` class
  // above the specified breakpoint — preserving the desktop-first cascade.
  const textClasses = responsiveClasses(withBaseDefault(variant, 'default-400'), variantToClasses);

  return (
    <Tag
      id={id}
      className={modifierClasses(textClasses, maxLines, color, className)}    >
      {children}
    </Tag>
  );
}

export function RCTextDisplay({
  htmlTag = 'div',
  size = '1600',
  weight = 'bold',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<DisplayTextProps>) {
  const Tag = htmlTag as React.ElementType;

  return (
    <Tag
      id={id}
      className={getClassnames('display', size, weight, maxLines, color, className)}    >
      {children}
    </Tag>
  );
}

function RCTextEyebrow({
  htmlTag = 'div',
  size = '300',
  weight = 'bold',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<EyebrowTextProps>) {
  const Tag = htmlTag as React.ElementType;

  return (
    <Tag
      id={id}
      className={getClassnames('eyebrow', size, weight, maxLines, color, className)}    >
      {children}
    </Tag>
  );
}

function RCTextFine({
  htmlTag = 'div',
  size = '300',
  weight = 'regular',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<FineTextProps>) {
  const Tag = htmlTag as React.ElementType;

  return (
    <Tag
      id={id}
      className={getClassnames('fine', size, weight, maxLines, color, className)}    >
      {children}
    </Tag>
  );
}

export function RCTextTitle({
  htmlTag = 'div',
  size = '900',
  weight = 'bold',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<TitleTextProps>) {
  const Tag = htmlTag as React.ElementType;

  return (
    <Tag
      id={id}
      className={getClassnames('title', size, weight, maxLines, color, className)}    >
      {children}
    </Tag>
  );
}

export function RCTextUtility({
  htmlTag = 'div',
  size = '400',
  weight = 'regular',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<UtilityTextProps>) {
  const Tag = htmlTag as React.ElementType;

  return (
    <Tag
      id={id}
      className={getClassnames('utility', size, weight, maxLines, color, className)}    >
      {children}
    </Tag>
  );
}

export function RCTextDefault({
  htmlTag = 'div',
  size = '400',
  weight = 'regular',
  color = undefined,
  maxLines = undefined,
  id = undefined,
  className = undefined,
  children,
}: React.PropsWithChildren<DefaultTextProps>) {
  const Tag = htmlTag as React.ElementType;

  return (
    <Tag
      id={id}
      className={getClassnames('default', size, weight, maxLines, color, className)}    >
      {children}
    </Tag>
  );
}

RCText.Default = RCTextDefault;
RCText.Display = RCTextDisplay;
RCText.Eyebrow = RCTextEyebrow;
RCText.Fine = RCTextFine;
RCText.Title = RCTextTitle;
RCText.Utility = RCTextUtility;


// Used by Storybook

interface validOutputProps {
  [variant: string]: {
    htmlTags: readonly string[],
    sizes: readonly string[],
    weights: readonly string[],
  }
}

const outputProps = propOptions as validOutputProps;

const combinedVariants = Object.keys(outputProps).map(variant => ({
  [variant]: {
    id: variant,
    weights: Array.prototype.reverse.call([...outputProps[variant].weights]),
    sizes: Array.prototype.reverse.call([...outputProps[variant].sizes]),
    defaultWeight: propDefaults[variant].weight,
    defaultSize: propDefaults[variant].size,
  },
}));

// data object for storybook
export const TextVariantProperties = combinedVariants.reduce((output, variant) => ({ ...output, ...variant }), {});
